import React from 'react';
import { Paper, Table, TableRow, TableCell, TableHead, TableBody, makeStyles, Theme, Icon } from '@material-ui/core';
import clsx from 'clsx';
import { BaseListCompFieldsSet, BaseListCompField, BaseListCompFieldsSettings, createHeaderColumn, BaseListCompProps, useModeTableStyle,
    useModePanelStyle, BaseListCompDataItem, CompPanelHeader, createPreviewItemCell } from '@moderepo/mode-app-components';
import { Entity } from '@moderepo/mode-apis';
import { KPIStatus } from '../../containers';
import { NO_VALUE } from '../../utils/constants';


const useStyle = makeStyles((theme: Theme) => {
    return {

        panelHeaderIcon: {
        },

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        rankingDate: {
            fontWeight : 'bold',
            marginRight: theme.spacing(1),
        },

        tableContainer: {
            overflow: 'auto',
            flex    : 1,
        },

        tableRow: {
            '&.highlight': {
                '& .name-col': {
                    fontWeight: 'bold',
                },
            },
            '&.empty': {
                opacity: 0.3,
            },
        },

        tableCol: {
            '&.rank-col': {
                textAlign : 'right',
                fontWeight: 'bold',
            },
            '&.name-col': {
                textAlign: 'left',
                width    : '100%',
            },
            '&.metric-value-col': {
                textAlign      : 'center',
                '&.status-good': {
                    color: theme.palette.success.main,
                },
                '&.status-bad': {
                    color: theme.palette.error.main,
                },
            },
            '&.rankChange-col': {
                textAlign: 'center',
                '& .icon': {
                    color        : theme.palette.text.primary,
                    '&.increased': {
                        color: theme.palette.error.main,    // for ranking, increased means bad
                    },
                    '&.decreased': {
                        color: theme.palette.success.main,  // for ranking, decreased means good
                    },
                },
            },
            '&.sorted-column': {
                fontWeight: 'bold',
            },
        },
    };
}, { name: 'EntitiesRankingTable' });


const DEFAULT_RANKING_LIMIT = 5;

/**
 * A data structure to store all the ranking information needed for EACH entity. The table need these info for display.
 */
export interface EntityRankInfo {
    readonly entity: Entity;

    // The different between the current rank and previous rank. For example, if the entity's current rank is 4th and previously it's rank
    // is 6th then that mean rankChange will be -2 which mean it's rank increased by 2. NOTE: Rank values are in opposite order so negative
    // rankChange means good
    readonly rankChange: number;

    // The metric key/value pair for which the key is the metric's name and value is the value for that metric
    readonly metricsValueAndStatus: {
        readonly [metricName: string]: {
            readonly value: number;
            readonly status: KPIStatus;
        } | undefined;
    }
}



/**
 * Table columns setting for Ranking table. Ranking table columns settings MUST contain these column keys
 */
export interface EntitiesRankingTableFieldsSet extends BaseListCompFieldsSet {
    readonly rank?: BaseListCompField;
    readonly name?: BaseListCompField;
    readonly rankChange?: BaseListCompField;
}


/**
 * The props required for the EntitiesRankingTable component in addition to the props inherited from BaseListCompProps
 */
export interface EntitiesRankingTableProps extends BaseListCompProps<EntityRankInfo> {
    readonly listData: readonly BaseListCompDataItem<EntityRankInfo>[] | undefined;
    readonly fieldsSettings: BaseListCompFieldsSettings<EntitiesRankingTableFieldsSet>;

    readonly date?: string | undefined;

    // The OPTIONAL entity the user is looking at. If this is provided and if this entity exist in the list of listData, we will high light
    // the row this entity belonged to to make it easier to find the entity from the list
    readonly currentEntity?: Entity | undefined;

    // Max # of items to show. If this is specified and if listData has less then the max, we will still show all the rows up to the max
    // but the rows that don't have data will be drawn blank
    readonly maxItems?: number | undefined;
}


/**
 * This is a DUMB component that is used for displaying the list of entities' ranking. All this component does is display data
 * provided through props. This component does not have any logic and any user action will be dispatched to the container's handlers.
 */
export const EntitiesRankingTable: React.FC<EntitiesRankingTableProps> = (props: EntitiesRankingTableProps) => {

    const tableClasses = useModeTableStyle();
    const panelClasses = useModePanelStyle();
    const classes = useStyle();

    const itemsCount = props.maxItems !== undefined && props.maxItems > 1 && props.listData && props.maxItems > props.listData.length
        ? props.maxItems                    // There are less items in listData than maxItems so we will display maxItems of items
        : Math.max(props.listData?.length ?? 0, DEFAULT_RANKING_LIMIT);    // Display all items in listData, with at least DEFAULT_RANKING_LIMIT

    return (
        <Paper elevation={2} className={clsx(panelClasses.root, props.className)}>
            <CompPanelHeader
                {...props}
                customContent={(
                    <>
                        {props.date && (
                            <div className={classes.rankingDate}>{props.date}</div>
                        )}
                    </>
                )}
            />

            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>

                {props.listData && props.listData.length > 0 && (
                    <div className={classes.tableContainer}>
                        <Table className={tableClasses.root} size="medium">
                            <TableHead className={tableClasses.tableHeader}>
                                <TableRow className={tableClasses.tableHeaderRow}>
                                    {/* filter out hidden columns first and then go through each column to create Header Cell */}
                                    {Object.keys(props.fieldsSettings.fields).filter((fieldName: string) => {
                                        return !props.fieldsSettings.fields[fieldName]?.hidden;
                                    }).map((fieldName: string) => {
                                        const field = props.fieldsSettings.fields[fieldName];
                                        if (field) {
                                            return createHeaderColumn(props.fieldsSettings, fieldName, field,
                                                clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`,
                                                    props.fieldsSettings.currentSortedField === fieldName && 'sorted-column'));
                                        }
                                        return <></>;
                                    })}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {(new Array(itemsCount)).fill(0).map((_, index) => {
                                    const dataItem = props.listData?.[index];
                                    return (
                                        <TableRow
                                            className={clsx(
                                                tableClasses.tableBodyRow, classes.tableRow, 'clickable', dataItem?.selected && 'selected',
                                                props.currentEntity?.entityId === dataItem?.actualValue.entity.entityId && 'highlight',
                                                !dataItem && 'empty',
                                            )}
                                            key={dataItem ? dataItem.actualValue.entity.entityId : index}
                                            onClick={() => {
                                                if (dataItem && props.onListItemSelected) {
                                                    props.onListItemSelected(dataItem.actualValue);
                                                }
                                            }}
                                        >
                                            {/* filter out hidden columns first and then go through each column to create Table Cell */}
                                            {Object.keys(props.fieldsSettings.fields).filter((fieldName: string) => {
                                                return !props.fieldsSettings.fields[fieldName]?.hidden;
                                            }).map((fieldName: string) => {

                                                // Create table cell for the Rank field
                                                if (fieldName === 'rank' && props.fieldsSettings.fields.rank) {
                                                    return (
                                                        <TableCell
                                                            key={fieldName}
                                                            className={clsx(
                                                                tableClasses.tableCol, classes.tableCol, 'rank-col',
                                                            )}
                                                        >
                                                            {index + 1}.
                                                        </TableCell>
                                                    );
                                                }

                                                // If not data item, return empty table cell except for the Rank column. Therefore, this should
                                                // be AFTER the 'rank' fieldName
                                                if (!dataItem) {
                                                    return (
                                                        <TableCell key={fieldName} className={clsx(tableClasses.tableCol, classes.tableCol)} />
                                                    );
                                                }

                                                // Create table cell for the Name field
                                                if (fieldName === 'name' && props.fieldsSettings.fields.name) {
                                                    return (
                                                        <TableCell
                                                            key={fieldName}
                                                            className={clsx(
                                                                tableClasses.tableCol, classes.tableCol, 'name-col',
                                                            )}
                                                        >
                                                            {dataItem.actualValue.entity.displaySettings.name ?? ''}
                                                        </TableCell>
                                                    );
                                                }

                                                // Create table cell for the Rank Change field
                                                if (fieldName === 'rankChange' && props.fieldsSettings.fields.rankChange) {
                                                    return (
                                                        <TableCell
                                                            key={fieldName}
                                                            className={clsx(
                                                                tableClasses.tableCol, classes.tableCol, 'rankChange-col',
                                                            )}
                                                        >
                                                            <div className={clsx(
                                                                'icon',
                                                                dataItem.actualValue.rankChange < 0 && 'decreased',
                                                                dataItem.actualValue.rankChange > 0 && 'increased',
                                                            )}
                                                            >
                                                                {dataItem.actualValue.rankChange < 0 && <Icon>expand_less</Icon>}
                                                                {dataItem.actualValue.rankChange > 0 && <Icon>expand_more</Icon>}
                                                                {dataItem.actualValue.rankChange === 0 && <Icon>remove</Icon>}
                                                            </div>
                                                        </TableCell>
                                                    );
                                                }

                                                // Create table cell for the Preview field
                                                if (fieldName === 'preview' && props.fieldsSettings.fields.preview) {
                                                    return createPreviewItemCell(
                                                        dataItem, clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`),
                                                        props.onPreviewItemClicked,
                                                    );
                                                }

                                                // Create table cell for the other fields, the fields for the metrics
                                                if (props.fieldsSettings.fields[fieldName]) {
                                                    const valueAndStatus = dataItem.actualValue.metricsValueAndStatus[fieldName];
                                                    return (
                                                        <TableCell
                                                            key={fieldName}
                                                            className={clsx(
                                                                tableClasses.tableCol, classes.tableCol, 'metric-value-col',
                                                                props.fieldsSettings.currentSortedField === fieldName && 'sorted-column',
                                                                `status-${valueAndStatus?.status}`,
                                                            )}
                                                        >
                                                            {valueAndStatus?.value ?? NO_VALUE}
                                                        </TableCell>
                                                    );
                                                }

                                                return <></>;
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {props.paginationComp}
                    </div>
                )}

                {(!props.listData || props.listData?.length === 0) && props.noDataMessageComp}
            </div>
        </Paper>
    );
};
